import {Injectable, Injector} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {registerLocaleData} from '@angular/common';
import * as moment from 'moment';
import {EQOIcon, EQORebrandIcon} from '@quickorder/angular-web-lib';

export interface ILangToLocales {
	[locale: string]: {
		angularLocale: string;
		momentLocale: string;
	};
}

export interface ILocales {
	angularLocale: string;
	momentLocale: string;
}

export interface ILanguage {
	language_id?: number;
	angularKey: string | undefined;
	translateAble: boolean;
	locale: string;
	locales: ILocales | undefined;
	language: string;
	flag: string;
	phone_ext: string;
	webLibIcon?: EQOIcon;
	country_code?: string;
	zendeskLocale?: string;
	rebrandIcon: EQORebrandIcon;
}

@Injectable()
export class InternationalizationService {
	public readonly navigatorLangToLanguage: { [navigatorLang: string]: string };
	private _timezone: string = 'America/Los_Angeles';
	private _lang: ILanguage;
	private readonly langToLocales: ILangToLocales;
	private readonly _languages: ILanguage[];
	private _currentLocales: ILocales;

	constructor(private injector: Injector) {
		this.langToLocales = {
			'da-DK': {
				angularLocale: 'da',
				momentLocale: 'da',
			},
			'nb-NO': {
				angularLocale: 'nb',
				momentLocale: 'nb',
			},
			'en-GB': {
				angularLocale: 'en-GB',
				momentLocale: 'en-gb',
			},
			'en-US': {
				angularLocale: 'en-US',
				momentLocale: 'en',
			},
		};
		this._languages = [
			{
				language_id: 1,
				angularKey: 'en',
				locale: 'en-US',
				locales: this.langToLocales['en-US'],
				flag: '🇺🇸',
				language: 'English',
				phone_ext: '+1',
				translateAble: true,
				webLibIcon: EQOIcon.FLAG_US,
				zendeskLocale: 'en',
				rebrandIcon: EQORebrandIcon.flag_us,
			},
			{
				language_id: 2,
				angularKey: 'en',
				locale: 'en-GB',
				locales: this.langToLocales['en-GB'],
				flag: '🇬🇧',
				language: 'English',
				phone_ext: '+44',
				translateAble: true,
				webLibIcon: EQOIcon.FLAG_UK,
				zendeskLocale: 'en',
				rebrandIcon: EQORebrandIcon.flag_gb,
			},
			{
				language_id: 3,
				angularKey: 'da',
				locale: 'da-DK',
				locales: this.langToLocales['da-DK'],
				flag: '🇩🇰',
				language: 'Dansk',
				phone_ext: '+45',
				translateAble: true,
				webLibIcon: EQOIcon.FLAG_DK,
				zendeskLocale: 'da',
				rebrandIcon: EQORebrandIcon.flag_dk,
			},
			{
				language_id: 4,
				angularKey: 'nb',
				locale: 'nb-NO',
				locales: this.langToLocales['nb-NO'],
				flag: '🇳🇴',
				language: 'Norsk (bokmål)',
				phone_ext: '+47',
				translateAble: true,
				webLibIcon: EQOIcon.FLAG_NO,
				zendeskLocale: 'no-no',
				rebrandIcon: EQORebrandIcon.flag_no,
			},
			{
				angularKey: undefined,
				locale: 'sv-SE',
				locales: undefined,
				flag: '🇸🇪',
				language: 'Sverige',
				phone_ext: '+46',
				translateAble: false,
				rebrandIcon: EQORebrandIcon.flag_no,
			},
			{
				angularKey: undefined,
				locale: 'de-DE',
				locales: undefined,
				flag: '🇩🇪',
				language: 'Deutschland',
				phone_ext: '+49',
				translateAble: false,
				rebrandIcon: EQORebrandIcon.flag_no,
			},
			{
				angularKey: undefined,
				locale: 'nl-NL',
				locales: undefined,
				flag: '🇳🇱',
				language: 'Nederland',
				phone_ext: '+31',
				translateAble: false,
				rebrandIcon: EQORebrandIcon.flag_no,
			},
			{
				angularKey: undefined,
				locale: 'fr-FR',
				locales: undefined,
				flag: '🇫🇷',
				language: 'France',
				phone_ext: '+33',
				translateAble: false,
				rebrandIcon: EQORebrandIcon.flag_no,
			},
			{
				angularKey: undefined,
				locale: 'it-IT',
				locales: undefined,
				flag: '🇮🇹',
				language: 'Italia',
				phone_ext: '+39',
				translateAble: false,
				rebrandIcon: EQORebrandIcon.flag_no,
			},
			{
				angularKey: undefined,
				locale: 'de-AT',
				locales: undefined,
				flag: '🇦🇹',
				language: 'Österreich',
				phone_ext: '+43',
				translateAble: false,
				rebrandIcon: EQORebrandIcon.flag_no,
			},
			{
				angularKey: undefined,
				locale: 'de-CH',
				locales: undefined,
				flag: '🇨🇭',
				language: 'Schweiz',
				phone_ext: '+41',
				translateAble: false,
				rebrandIcon: EQORebrandIcon.flag_no,
			},
			{
				angularKey: undefined,
				locale: 'pl-PL',
				locales: undefined,
				flag: '🇵🇱',
				language: 'Polska',
				phone_ext: '+48',
				translateAble: false,
				rebrandIcon: EQORebrandIcon.flag_no,
			},
		];
		this.navigatorLangToLanguage = {
			da: 'da-DK',
			fr: 'fr-FR',
			/*en: 'en-US',*/
			en: 'en-GB',
			nb: 'nb-NO',
			sv: 'sv-SE',
			de: 'de-DE',
			nl: 'nl-NL',
			it: 'it-IT',
			pl: 'pl-PL',
		};
		this._lang = this._languages[0];
		this.translate.setDefaultLang(this._lang.angularKey);
		this._currentLocales = this._lang.locales; // set first currentLocales (always initialized)
	}

	public get available_languages(): ILanguage[] {
		return this._languages.filter((lang) => lang.translateAble);
	}

	public get languages(): any {
		return this._languages;
	}

	public get currentLocales(): any { // while the new localeData is loading the current locales is the previous loaded locales
		return this._currentLocales;
	}

	public get translate(): TranslateService {
		return this.injector.get(TranslateService);
	}

	public setLang(locale: string): void {
		const lang = this.available_languages.find((language) =>
			language.locale === locale);
		this._lang = (lang)
			? lang
			: this._languages[0];
		this.translate.use(this._lang.angularKey);
		this.setLocale(this._lang.locales);
	}

	public set lang(lang: ILanguage) {
		this.setLang(lang.locale);
	}

	public get lang(): ILanguage  {
		return this._lang;
	}

	public getCurrentLanguage(): ILanguage {
		return this._lang;
	}

	public set timezone(timezone: string) {
		this._timezone = timezone;
	}

	public get timezone(): string {
		return this._timezone;
	}

	private setLocale(locales: ILocales): void {
		if (locales.angularLocale === 'en-US') {
			moment.locale(locales.momentLocale);
			this._currentLocales.momentLocale = locales.momentLocale;
			this._currentLocales.angularLocale = locales.angularLocale;
		} else {
			import(
				/* webpackInclude: /(nb|da|en\-gb)\.js$/ */
				`moment/locale/${locales.momentLocale}.js`).then(() => {
				moment.locale(locales.momentLocale);
				this._currentLocales.momentLocale = locales.momentLocale;
			});
			import(
				/* webpackInclude: /(nb|da|en\-GB)\.js$/ */
				`@angular/common/locales/${locales.angularLocale}.js`).then(module => {
				import(
					/* webpackInclude: /(nb|da|en\-GB)\.js$/ */
					`@angular/common/locales/extra/${locales.angularLocale}.js`).then(extraModule => {
					registerLocaleData(module.default, extraModule.default);
					this._currentLocales.angularLocale = locales.angularLocale;
				});
			});
		}
	}
}
